import { Breadcrumbs } from '@components/display/breadcrumbs';
import { MaxWidthWrapper } from '@components/layout/MaxWidthWrapper';
import { HeaderFeaturesWrapper } from '@components/layout/public/HeaderFeaturesWrapper';
import { SearchToolbar } from '@components/layout/search';
import { APP_PREFIX_PATH } from '@constants/common';
import { SearchScopes } from '@constants/search';
import { SearchContext } from '@contexts/SearchContext';
import { useAppSelector } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import { useDebounce } from '@hooks/useDebounce';
import AppsIcon from '@mui/icons-material/Apps';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { AppBar, Badge, Box, IconButton, Stack, TabsActions, Toolbar } from '@mui/material';
import { PublicNavbar } from '@navbar/components/PublicNavbar';
import { NotificationsBoxContext } from '@notifications/contexts';
import { useNotifications } from '@notifications/hooks';
import { selectBreadcrumbs } from '@store/breadcrumbSlice';
import { getBreadcrumbsFromPath } from '@utils/route';
import clsx from 'clsx';
import { PropsWithChildren, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import {
    PublicLayoutOptionsContext,
    type ActivePublicLayoutOptions,
    type PublicLayoutRouteOptions,
} from './PublicLayoutOptionsContext';

type PublicLayoutProps = PropsWithChildren<PublicLayoutRouteOptions>;

const areRouteOptionsEqual = (left: PublicLayoutRouteOptions, right: PublicLayoutRouteOptions) =>
    left.useMaxWidthWrapper === right.useMaxWidthWrapper &&
    left.dottedBackgroundProps === right.dottedBackgroundProps &&
    left.fullWidthTopContent === right.fullWidthTopContent &&
    left.enableHeaderFeatures === right.enableHeaderFeatures &&
    left.extraToolbar === right.extraToolbar &&
    left.hideSearchBar === right.hideSearchBar &&
    left.hideChrome === right.hideChrome;

const usePublicLayoutOptions = (options: PublicLayoutRouteOptions) => {
    const setActiveOptions = useContext(PublicLayoutOptionsContext);
    const { pathname } = useLocation();

    if (!setActiveOptions) {
        throw new Error('usePublicLayoutOptions must be used inside PublicLayout');
    }

    useLayoutEffect(() => {
        const owner = Symbol('public-layout-route-options');
        setActiveOptions((activeOptions) =>
            activeOptions?.pathname === pathname && areRouteOptionsEqual(activeOptions.options, options)
                ? activeOptions
                : { owner, pathname, options }
        );

        return () => {
            setActiveOptions((activeOptions) => (activeOptions?.owner === owner ? null : activeOptions));
        };
    }, [
        options.dottedBackgroundProps,
        options.fullWidthTopContent,
        options.enableHeaderFeatures,
        options.extraToolbar,
        options.hideChrome,
        options.hideSearchBar,
        options.useMaxWidthWrapper,
        pathname,
        setActiveOptions,
    ]);
};

const PublicLayoutOptions = ({ children, ...options }: PropsWithChildren<PublicLayoutRouteOptions>) => {
    usePublicLayoutOptions(options);
    return <>{children}</>;
};

const PublicLayout = (props: PublicLayoutProps) => {
    const { pathname } = useLocation();
    const [activeOptions, setActiveOptions] = useState<ActivePublicLayoutOptions | null>(null);
    const routeOptions = activeOptions?.pathname === pathname ? activeOptions.options : {};

    return (
        <PublicLayoutOptionsContext.Provider value={setActiveOptions}>
            <PublicLayoutFrame {...props} {...routeOptions}>
                {props.children ?? <Outlet />}
            </PublicLayoutFrame>
        </PublicLayoutOptionsContext.Provider>
    );
};

const PublicLayoutFrame = ({
    children,
    useMaxWidthWrapper = true,
    dottedBackgroundProps,
    fullWidthTopContent,
    enableHeaderFeatures = true,
    extraToolbar,
    hideSearchBar = false,
    hideChrome = false,
}: PublicLayoutProps) => {
    const { pathname } = useLocation();
    const breadcrumbs = useAppSelector(selectBreadcrumbs);
    const intl = useIntl();
    const currentPath = pathname.replace(/\/+$/, '') || '/';
    const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
    const lastBreadcrumbPath = lastBreadcrumb?.to.split(/[?#]/, 1)[0].replace(/\/+$/, '') || '';
    const hasCurrentBreadcrumbs =
        currentPath === lastBreadcrumbPath ||
        (breadcrumbs.length > 1 &&
            lastBreadcrumbPath !== APP_PREFIX_PATH &&
            currentPath.startsWith(`${lastBreadcrumbPath}/`));
    const currentBreadcrumbs = hasCurrentBreadcrumbs ? breadcrumbs : getBreadcrumbsFromPath(pathname, intl);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce({ value: search, delay: 500 });
    const [showSearch, setShowSearch] = useState(false);
    const [scope, setScope] = useState<string | undefined>(undefined);
    const location = useLocation();
    const { user } = useAuth();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showApps, setShowApps] = useState(false);
    const [showNavigation, setShowNavigation] = useState(false);

    const currentScopeKey = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        return pathSegments.find((segment) => segment in SearchScopes);
    }, [location.pathname]);

    useEffect(() => {
        setSearch('');
        setShowSearch(false);
        setShowNotifications(false);
        setShowApps(false);
        setShowNavigation(false);
    }, [pathname]);

    const handleSearchFocus = () => {
        if (!scope && !search && currentScopeKey) {
            setScope(currentScopeKey);
        }
    };

    const handleScopeDelete = () => {
        setScope(undefined);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
    };

    const Content = useMemo(
        () =>
            useMaxWidthWrapper ? (
                <MaxWidthWrapper
                    dottedBackgroundProps={dottedBackgroundProps}
                    fullWidthTopContent={fullWidthTopContent}
                    scrollResetKey={pathname}
                >
                    {children}
                </MaxWidthWrapper>
            ) : (
                children
            ),
        [children, dottedBackgroundProps, fullWidthTopContent, useMaxWidthWrapper]
    );

    const navWidth = 250;

    const handleNavigationToggle = () => {
        setShowNavigation((show) => !show);
        setShowApps(false);
        setShowNotifications(false);
        setShowSearch(false);
        setSearch('');
    };

    //#region Notifications
    const {
        unreadNotificationsTotalCount,
        setUnreadNotificationsTotalCount,
        readNotifications,
        setReadNotifications,
        unreadNotifications,
        setUnreadNotifications,
        isReadActionRunning,
        setIsReadActionRunning,
        hasNotifications,
        handleMarkAllAsRead,
        isMarkAllReadLoading,
        processedStreamIdsRef,
    } = useNotifications();
    const tabsActionRef = useRef<TabsActions>(null);
    //#endregion

    if (hideChrome) {
        return <>{children}</>;
    }

    return (
        <SearchContext.Provider value={{ search: debouncedSearch }}>
            <NotificationsBoxContext.Provider
                value={{
                    unreadNotificationsTotalCount: unreadNotificationsTotalCount,
                    setUnreadNotificationsTotalCount: setUnreadNotificationsTotalCount,
                    isReadActionRunning,
                    setIsReadActionRunning,
                    readNotificationsState: [readNotifications, setReadNotifications],
                    unreadNotificationsState: [unreadNotifications, setUnreadNotifications],
                    tabsActionRef,
                    isMarkAllReadLoading,
                    handleMarkAllAsRead,
                    hasNotifications,
                    processedStreamIdsRef,
                }}
            >
                <div className="flex h-screen">
                    <AppBar
                        position="fixed"
                        elevation={0}
                        className="bg-white topMenu-bar"
                        sx={{
                            px: '20px',
                            height: '70px',
                            display: { xs: showSearch ? 'block' : 'flex', md: 'none' },
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            boxShadow: '0 4px 6px 0 rgba(33, 37, 41, 0.20), 0 0 1px 0 rgba(33, 37, 41, 0.32)',
                            zIndex: 700,
                        }}
                    >
                        {!showSearch ? (
                            <>
                                {/* Left side: Logo */}
                                <a href="/" className="d-inline-block">
                                    <img src="/static/img/victron-logo-header.svg" alt="Logo" style={{ height: 32 }} />
                                </a>

                                {/* Right side: Search + Menu buttons (only on mobile) */}
                                <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                                    {enableHeaderFeatures && (
                                        <>
                                            <IconButton
                                                className="p-0"
                                                onClick={() => {
                                                    setShowApps((show) => !show);
                                                    setShowNotifications(false);
                                                    setShowNavigation(false);
                                                }}
                                            >
                                                <AppsIcon
                                                    sx={{ fontSize: 25 }}
                                                    color="black"
                                                    className={clsx('transition-colors', {
                                                        'text-primary-600': showApps,
                                                    })}
                                                />
                                            </IconButton>
                                            <IconButton
                                                className="p-0"
                                                onClick={() => {
                                                    setShowNotifications((show) => !show);
                                                    setShowApps(false);
                                                    setShowNavigation(false);
                                                }}
                                            >
                                                <Badge
                                                    color="error"
                                                    variant="dot"
                                                    overlap="circular"
                                                    invisible={!hasNotifications && unreadNotificationsTotalCount === 0}
                                                >
                                                    <NotificationsOutlinedIcon
                                                        sx={{ fontSize: 25 }}
                                                        color="black"
                                                        className={clsx('transition-colors', {
                                                            'text-primary-600': showNotifications,
                                                        })}
                                                    />
                                                </Badge>
                                            </IconButton>
                                            <IconButton
                                                onClick={() => {
                                                    setShowSearch(true);
                                                    setShowNavigation(false);
                                                    setShowApps(false);
                                                    setShowNotifications(false);
                                                }}
                                                className="p-0"
                                            >
                                                <SearchIcon color="black" sx={{ fontSize: 25 }} />
                                            </IconButton>
                                        </>
                                    )}
                                    <IconButton onClick={handleNavigationToggle} className="mobile__menu-trigger p-0">
                                        <MenuIcon
                                            color="black"
                                            sx={{ fontSize: 25 }}
                                            className={clsx('transition-colors', {
                                                'text-primary-600': showNavigation,
                                            })}
                                        />
                                    </IconButton>
                                </Stack>
                            </>
                        ) : (
                            <Stack direction="row" gap={1} alignItems="center" height="100%">
                                <SearchToolbar
                                    search={search}
                                    onSearchChange={handleSearchChange}
                                    onSearchFocus={handleSearchFocus}
                                    scopeLabel={scope ? intl.formatMessage(SearchScopes[scope]) : undefined}
                                    onScopeDelete={handleScopeDelete}
                                    user={user}
                                />
                                <IconButton onClick={() => setShowSearch(false)} size="small" sx={{ flexShrink: 0 }}>
                                    <CloseIcon color="black" />
                                </IconButton>
                            </Stack>
                        )}
                    </AppBar>
                    <Box
                        component="nav"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            width: { md: navWidth },
                            height: '100vh',
                            flexShrink: 0,
                        }}
                    >
                        <PublicNavbar />
                    </Box>
                    <Box
                        component="main"
                        sx={{ width: { md: `calc(100% - ${navWidth}px)`, xs: '100%' }, flexGrow: 1 }}
                        className={clsx('bg-cultured', { 'flex flex-col': useMaxWidthWrapper })}
                    >
                        <Toolbar className="h-[70px]" sx={{ display: { md: 'none' }, flexShrink: 0 }} />
                        {enableHeaderFeatures || showNavigation ? (
                            <HeaderFeaturesWrapper
                                searchState={[search, setSearch]}
                                notifications={Boolean(enableHeaderFeatures && showNotifications)}
                                apps={Boolean(enableHeaderFeatures && showApps)}
                                scope={scope}
                                scopeLabel={scope ? intl.formatMessage(SearchScopes[scope]) : undefined}
                                onSearchFocus={handleSearchFocus}
                                onScopeDelete={handleScopeDelete}
                                user={user}
                                extraToolbar={
                                    enableHeaderFeatures
                                        ? extraToolbar ?? <Breadcrumbs path={currentBreadcrumbs} />
                                        : undefined
                                }
                                hideSearchBar={!enableHeaderFeatures || hideSearchBar}
                                navigation={showNavigation}
                                navigationContent={<PublicNavbar onClose={() => setShowNavigation(false)} />}
                            >
                                {Content}
                            </HeaderFeaturesWrapper>
                        ) : (
                            Content
                        )}
                    </Box>
                </div>
            </NotificationsBoxContext.Provider>
        </SearchContext.Provider>
    );
};

export { PublicLayout, PublicLayoutOptions, usePublicLayoutOptions };
